import React from "react";
import { HardDrive, Cloud, Database, AlertTriangle } from "lucide-react";
import { api, fmtBytes } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";

const Tile = ({ icon: Icon, label, value, sub, tone, id }) => (
  <div className="border border-[#252A3B] rounded-lg p-3" data-testid={id}>
    <div className="flex items-center gap-2 metric-label"><Icon size={13} className={tone} />{label}</div>
    <div className={`font-head text-xl font-bold mt-1 ${tone}`}>{value}</div>
    {sub && <div className="metric-label mt-0.5">{sub}</div>}
  </div>
);

const Bar = ({ used, total }) => (
  <div className="h-1 bg-[#252A3B] rounded mt-2"><div className="h-1 rounded bg-[#E6B04E]" style={{ width: `${Math.min(100, used / total * 100)}%` }} /></div>
);

export const StorageWarning = ({ s }) => s?.warning ? (
  <div className="flex items-center gap-2 border border-[#fbbf24]/40 bg-[#fbbf24]/10 rounded p-3 text-sm" data-testid="storage-warning">
    <AlertTriangle size={15} className="text-[#fbbf24] shrink-0" />{s.warning}
  </div>
) : null;

export const useStorage = () => usePoll(() => api.storageStatus(), 60000, []).data;

export const StorageMeter = () => {
  const s = useStorage();
  if (!s) return null;
  const d = s.disk;
  return (
    <div className="studio-card p-5 space-y-4" data-testid="storage-meter">
      <div className="flex items-center gap-2"><HardDrive size={16} className="text-[#E6B04E]" /><span className="font-head font-bold">Storage</span></div>
      <div className="grid md:grid-cols-3 gap-3">
        {s.mode === "cloud" ? (
          <>
            <Tile icon={Cloud} id="storage-cloud" label={`Cloud storage (${s.cloud.quota_gb} GB plan)`} value={s.cloud.full ? "Full" : "OK"}
              tone={s.cloud.full ? "text-[#EF4444]" : "text-[#10B981]"} sub={s.cloud.full ? "New files go to the shared database" : "New files go to cloud storage"} />
            <Tile icon={Database} id="storage-db" label="Shared database (overflow files)" value={fmtBytes(s.gridfs_bytes)}
              sub={`${s.gridfs_files} file(s) · database total ${fmtBytes(s.db?.storage_bytes || 0)}`} />
          </>
        ) : <Tile icon={HardDrive} id="storage-local" label="Mode" value="This PC" sub="Everything is stored on your disk" tone="text-[#10B981]" />}
        <div className="border border-[#252A3B] rounded-lg p-3" data-testid="storage-disk">
          <div className="flex items-center gap-2 metric-label"><HardDrive size={13} />{s.mode === "cloud" ? "This server's working disk" : "Footage disk"}</div>
          <div className="font-head text-xl font-bold mt-1">{fmtBytes(d.free)} free</div>
          <div className="metric-label mt-0.5">of {fmtBytes(d.total)}</div>
          <Bar used={d.total - d.free} total={d.total} />
        </div>
      </div>
      <StorageWarning s={s} />
    </div>
  );
};
